import { bountyReward, isBounty } from "./bounty.ts";
import { addSparks } from "./controller.ts";
import { clamp, findParticipant, type GameData, type Participant, type Shot } from "./model.ts";
import { randomBetween } from "./random.ts";
import { surgeSteal, type GameSettings } from "./settings.ts";

export const ELIMINATION_SCORE = 280;
export const RESPAWN_SECONDS = 3.5;
export const ELIMINATION_ENERGY_DROP = 8;

/**
 * Deliberate asymmetry carried over from the prototype: a bot loses score when it
 * is robbed, a human does not. The scoring model is being redesigned around real
 * players, so this stays as-is instead of being "corrected" here.
 */
export const STEAL_VICTIM_PENALTY = 35;

/**
 * The arena wall, in one place.
 *
 * There used to be three ideas of where it was: the line the renderer drew (10, 76),
 * the fence movement clamped to (the whole orbit plus eight pixels at the sides, a
 * flat 102 at the bottom) and the box shots died in (12, 70). A core with a full
 * orbit was held a hundred and sixteen pixels from a line drawn at ten — you could
 * see the wall and not reach it.
 */
export const ARENA_TOP = 76;
export const ARENA_EDGE = 10;

/**
 * The camera stops at the world edge, so at the very bottom of the arena a core is
 * drawn at the bottom of the screen — where the HUD is. These bands keep it out
 * from under the chrome, and they are the only reason the wall is not symmetric.
 */
export const HUD_TOP_BAND = 30;
export const HUD_BOTTOM_BAND = 30;

/**
 * How far a core's *centre* may go. The core is what has to stay inside; the outer
 * spheres of a wide orbit may overhang the line, which is what they look like they
 * should do.
 */
export function arenaBounds(game: GameData, coreRadius: number) {
  return {
    minX: ARENA_EDGE + coreRadius,
    maxX: game.width - ARENA_EDGE - coreRadius,
    minY: ARENA_TOP + HUD_TOP_BAND,
    maxY: game.height - ARENA_EDGE - HUD_BOTTOM_BAND,
  };
}

export type ShotOutcome = { kind: "steal" | "eliminate"; victim: Participant; taken?: number };

export function isShotOutOfArena(shot: Shot, game: GameData) {
  return shot.x < ARENA_EDGE
    || shot.x > game.width - ARENA_EDGE
    || shot.y < ARENA_TOP
    || shot.y > game.height - ARENA_EDGE;
}

/**
 * Scatters a dead core's energy at the place it fell.
 *
 * The victim's *own* particles are relocated rather than new ones minted. Minting
 * grew the field with every death and, once it hit the cap, recycled whatever slot
 * came next — so eight particles elsewhere on the map silently teleported to the
 * corpse. With bots dying every few seconds that read as constant blinking across
 * the arena. Moving the victim's own energy keeps the field size fixed, keeps every
 * core's share intact, and matches the rule: a dead core drops *its colour*.
 */
export function dropEliminationEnergy(game: GameData, victim: Participant) {
  let moved = 0;
  for (const item of game.energy) {
    if (moved >= ELIMINATION_ENERGY_DROP) break;
    if (item.owner !== victim.id) continue;
    item.x = clamp(victim.x + randomBetween(game.random, -70, 70), 20, game.width - 20);
    item.y = clamp(victim.y + randomBetween(game.random, -70, 70), 80, game.height - 20);
    item.color = victim.color;
    moved += 1;
  }
}

/**
 * Applies one shot landing on one victim. The caller decides what the UI says about
 * it and whether the match should end, so this stays free of presentation concerns.
 */
export function resolveShotHit(
  game: GameData,
  shot: Shot,
  attacker: Participant,
  victim: Participant,
  settings: GameSettings,
): ShotOutcome {
  shot.returning = true;
  shot.life = 0;

  // What the shot asks for: one from an ordinary orb, 2, 3 or 4 from a ring.
  const wanted = shot.surge > 0 ? surgeSteal(shot.surge) : 1;
  const taken = Math.min(wanted, victim.orbs);
  // The whole payout is doubled against the marked core, steals and the kill alike.
  // Paying double only for the kill would have made it worth waiting for someone
  // else to strip them first, which is the opposite of what the mark is for.
  const hunted = isBounty(game, victim);

  if (taken > 0) {
    victim.orbs -= taken;
    if (victim.kind !== "human") victim.score = Math.max(0, victim.score - STEAL_VICTIM_PENALTY * taken);
    attacker.score += bountyReward(settings.stealScore * taken, hunted);
    shot.stolen = true;
    shot.taken = taken;
    shot.color = victim.color;
  }

  // An orbit absorbs what it can hold. Asked for more than it had and the surplus
  // reaches the core: a charged shot against a nearly-empty orbit takes what is
  // there *and* breaks it, rather than politely stopping at the last orb and
  // leaving a stripped core alive to be shot again.
  if (wanted <= taken) {
    addSparks(game, victim.x, victim.y, victim.color, 18);
    return { kind: "steal", victim, taken };
  }

  victim.alive = false;
  // Bots return to the arena; a human core ending the match is the deferred
  // asymmetry the user chose to keep for now.
  victim.respawn = victim.kind === "bot" ? RESPAWN_SECONDS : 0;
  attacker.score += bountyReward(ELIMINATION_SCORE, hunted);
  dropEliminationEnergy(game, victim);
  addSparks(game, victim.x, victim.y, victim.color, 34);
  return { kind: "eliminate", victim, taken };
}

export { findParticipant };
